/**
 * Mock Data for Development Without Backend
 *
 * Enable with VITE_MOCK_API=true in .env.local
 */

import type { ApiStudent, ApiWordBank, ApiWord } from '@/hooks/types';

const now = new Date().toISOString();

export const mockStudents: ApiStudent[] = [
  {
    id: 'student-1',
    teacher_id: 'mock-teacher',
    display_name: 'Alice Johnson',
    created_at: now,
    updated_at: now,
  },
  {
    id: 'student-2',
    teacher_id: 'mock-teacher',
    display_name: 'Bob Smith',
    created_at: now,
    updated_at: now,
  },
  {
    id: 'student-3',
    teacher_id: 'mock-teacher',
    display_name: 'Charlie Brown',
    created_at: now,
    updated_at: now,
  },
];

export const mockWords: ApiWord[] = [
  {
    id: 'word-1',
    word_bank_id: 'wordbank-1',
    text: 'cat',
    phoneme_count: 3,
    syllable_types: ['CVC'],
    expected_segmentation: ['c', 'a', 't'],
    tags: ['short vowel', 'CVC'],
    created_at: now,
    updated_at: now,
  },
  {
    id: 'word-2',
    word_bank_id: 'wordbank-1',
    text: 'dog',
    phoneme_count: 3,
    syllable_types: ['CVC'],
    expected_segmentation: ['d', 'o', 'g'],
    tags: ['short vowel', 'CVC'],
    created_at: now,
    updated_at: now,
  },
  {
    id: 'word-3',
    word_bank_id: 'wordbank-1',
    text: 'ship',
    phoneme_count: 3,
    syllable_types: ['CVC'],
    expected_segmentation: ['sh', 'i', 'p'],
    tags: ['digraph', 'short vowel'],
    created_at: now,
    updated_at: now,
  },
  {
    id: 'word-4',
    word_bank_id: 'wordbank-2',
    text: 'rain',
    phoneme_count: 3,
    syllable_types: ['CVV'],
    expected_segmentation: ['r', 'ai', 'n'],
    tags: ['vowel team', 'long vowel'],
    created_at: now,
    updated_at: now,
  },
  {
    id: 'word-5',
    word_bank_id: 'wordbank-2',
    text: 'boat',
    phoneme_count: 3,
    syllable_types: ['CVV'],
    expected_segmentation: ['b', 'oa', 't'],
    tags: ['vowel team', 'long vowel'],
    created_at: now,
    updated_at: now,
  },
];

export const mockWordBanks: ApiWordBank[] = [
  {
    id: 'wordbank-1',
    teacher_id: 'mock-teacher',
    name: 'CVC Words',
    created_at: now,
    updated_at: now,
    words: mockWords.filter(w => w.word_bank_id === 'wordbank-1'),
    wordCount: 3,
  },
  {
    id: 'wordbank-2',
    teacher_id: 'mock-teacher',
    name: 'Vowel Teams',
    created_at: now,
    updated_at: now,
    words: mockWords.filter(w => w.word_bank_id === 'wordbank-2'),
    wordCount: 2,
  },
];

// In-memory storage for mock CRUD operations
let students = [...mockStudents];
let wordBanks = [...mockWordBanks];
let words = [...mockWords];
let idCounter = 100;

const generateId = () => `mock-${++idCounter}`;

/**
 * Mock API handlers
 */
export const mockApi = {
  // Students
  'GET /api/students': () => ({
    status: 'success',
    data: { students, count: students.length },
  }),

  'POST /api/students': (body: { displayName: string }) => {
    const newStudent: ApiStudent = {
      id: generateId(),
      teacher_id: 'mock-teacher',
      display_name: body.displayName,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    students.push(newStudent);
    return { status: 'success', data: { student: newStudent } };
  },

  'PUT /api/students/:id': (body: { displayName: string }, params: { id: string }) => {
    const idx = students.findIndex(s => s.id === params.id);
    if (idx === -1) return { status: 'error', message: 'Student not found' };
    students[idx] = { ...students[idx], display_name: body.displayName, updated_at: new Date().toISOString() };
    return { status: 'success', data: { student: students[idx] } };
  },

  'DELETE /api/students/:id': (_body: unknown, params: { id: string }) => {
    students = students.filter(s => s.id !== params.id);
    return { status: 'success', message: 'Student deleted' };
  },

  // Word Banks
  'GET /api/word-banks': () => ({
    status: 'success',
    data: { wordBanks, count: wordBanks.length },
  }),

  'GET /api/word-banks/:id': (_body: unknown, params: { id: string }) => {
    const wb = wordBanks.find(w => w.id === params.id);
    if (!wb) return { status: 'error', message: 'Word bank not found' };
    return { status: 'success', data: { wordBank: { ...wb, words: words.filter(w => w.word_bank_id === params.id) } } };
  },

  'POST /api/word-banks': (body: { name: string }) => {
    const newWordBank: ApiWordBank = {
      id: generateId(),
      teacher_id: 'mock-teacher',
      name: body.name,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      words: [],
      wordCount: 0,
    };
    wordBanks.push(newWordBank);
    return { status: 'success', data: { wordBank: newWordBank } };
  },

  'PUT /api/word-banks/:id': (body: { name: string }, params: { id: string }) => {
    const idx = wordBanks.findIndex(w => w.id === params.id);
    if (idx === -1) return { status: 'error', message: 'Word bank not found' };
    wordBanks[idx] = { ...wordBanks[idx], name: body.name, updated_at: new Date().toISOString() };
    return { status: 'success', data: { wordBank: wordBanks[idx] } };
  },

  'DELETE /api/word-banks/:id': (_body: unknown, params: { id: string }) => {
    wordBanks = wordBanks.filter(w => w.id !== params.id);
    words = words.filter(w => w.word_bank_id !== params.id);
    return { status: 'success', message: 'Word bank deleted' };
  },

  // Words
  'POST /api/word-banks/:wordBankId/words': (body: { words: Array<{ text: string }> }, params: { wordBankId: string }) => {
    const newWords = body.words.map(w => ({
      id: generateId(),
      word_bank_id: params.wordBankId,
      text: w.text,
      phoneme_count: w.text.length,
      syllable_types: [],
      expected_segmentation: w.text.split(''),
      tags: [],
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }));
    words.push(...newWords);
    const wbIdx = wordBanks.findIndex(wb => wb.id === params.wordBankId);
    if (wbIdx !== -1) wordBanks[wbIdx].wordCount = (wordBanks[wbIdx].wordCount || 0) + newWords.length;
    return { status: 'success', data: { words: newWords, count: newWords.length } };
  },

  'DELETE /api/word-banks/:wordBankId/words/:wordId': (_body: unknown, params: { wordBankId: string; wordId: string }) => {
    words = words.filter(w => w.id !== params.wordId);
    const wbIdx = wordBanks.findIndex(wb => wb.id === params.wordBankId);
    if (wbIdx !== -1) wordBanks[wbIdx].wordCount = Math.max(0, (wordBanks[wbIdx].wordCount || 1) - 1);
    return { status: 'success', message: 'Word deleted' };
  },

  // Configurations (return empty for now)
  'GET /api/configurations': () => ({
    status: 'success',
    data: { configurations: {}, raw: [], count: 0 },
  }),

  'GET /api/configurations/:type': () => ({
    status: 'success',
    data: { configuration: null, config: {} },
  }),

  'PUT /api/configurations/:type': (body: { config: Record<string, unknown> }) => ({
    status: 'success',
    data: { configuration: { config: body.config }, config: body.config },
  }),

  // Health check
  'GET /health': () => ({
    status: 'ok',
    timestamp: new Date().toISOString(),
    uptime: 9999,
    environment: 'mock',
  }),
};

/**
 * Check if mock mode is enabled
 */
export const isMockEnabled = (): boolean => {
  return import.meta.env.VITE_MOCK_API === 'true';
};

/**
 * Match a request path to a mock handler
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type MockHandler = (body: any, params: Record<string, string>) => unknown;

export function matchMockRoute(method: string, path: string): { handler: MockHandler; params: Record<string, string> } | null {
  const routeKey = `${method} ${path}`;

  // Try exact match first
  if (mockApi[routeKey as keyof typeof mockApi]) {
    return { handler: mockApi[routeKey as keyof typeof mockApi] as MockHandler, params: {} };
  }

  // Try pattern matching for routes with params
  for (const [pattern, handler] of Object.entries(mockApi)) {
    const [patternMethod, patternPath] = pattern.split(' ');
    if (patternMethod !== method) continue;

    const patternParts = patternPath.split('/');
    const pathParts = path.split('/');

    if (patternParts.length !== pathParts.length) continue;

    const params: Record<string, string> = {};
    let matches = true;

    for (let i = 0; i < patternParts.length; i++) {
      if (patternParts[i].startsWith(':')) {
        params[patternParts[i].slice(1)] = pathParts[i];
      } else if (patternParts[i] !== pathParts[i]) {
        matches = false;
        break;
      }
    }

    if (matches) {
      return { handler: handler as MockHandler, params };
    }
  }

  return null;
}
